import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Shield, Clock, Award, Users, Wrench, ArrowRight, CheckCircle } from "lucide-react";
import Layout from "@/components/Layout";
import ServiceCard from "@/components/ServiceCard";
import FeatureCard from "@/components/FeatureCard";
import GoogleReviews from "@/components/GoogleReviews";
import AnimatedStats from "@/components/AnimatedStats";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import FloatingContactButton from "@/components/FloatingContactButton";
import ParticleBackground from "@/components/ParticleBackground";
import TypingEffect from "@/components/TypingEffect";
import ElevatorShaft from "@/components/ElevatorShaft";
import LocationWidget from "@/components/LocationWidget";
import { useLanguage } from "@/contexts/LanguageContext";
import heroImage from "@/assets/hero-elevator.jpg";
import maintenanceImage from "@/assets/maintenance.jpg";
import accessibilityImage from "@/assets/accessibility.jpg";
import freightImage from "@/assets/freight.jpg";
import modernizationImage from "@/assets/modernization.jpg";

const Index = () => {
  const { t } = useLanguage();

  const services = [
    {
      title: t("services.installation"),
      description: t("services.installation.desc"),
      image: modernizationImage,
      href: "/services/installation",
    },
    {
      title: t("services.maintenance"),
      description: t("services.maintenance.desc"),
      image: maintenanceImage,
      href: "/services/maintenance",
    },
    {
      title: t("services.modernization"),
      description: t("services.modernization.desc"),
      image: modernizationImage,
      href: "/services/modernization",
    },
    {
      title: t("services.accessibility"),
      description: t("services.accessibility.desc"),
      image: accessibilityImage,
      href: "/services/accessibility",
    },
    {
      title: t("services.freight"),
      description: t("services.freight.desc"),
      image: freightImage,
      href: "/services/freight",
    },
  ];

  const features = [
    {
      icon: Clock,
      title: t("features.since"),
      description: t("features.since.desc"),
    },
    {
      icon: Shield,
      title: t("features.quality"),
      description: t("features.quality.desc"),
    },
    {
      icon: Users,
      title: t("features.staff"),
      description: t("features.staff.desc"),
    },
    {
      icon: Wrench,
      title: t("features.service"),
      description: t("features.service.desc"),
    },
  ];

  const aboutItems = [
    t("about.cert"),
    t("about.staff"),
    t("about.equipment"),
    t("about.prices"),
  ];
  return (
    <Layout>
      <FloatingContactButton />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Σύγχρονος ανελκυστήρας LIFTCO"
            className="w-full h-full object-cover"
            fetchPriority="high"
            width="1920"
            height="1080"
          />
          <div className="absolute inset-0 bg-hero-gradient" />
          <ParticleBackground />
        </div>

        <div className="container relative z-10">
          <div className="max-w-3xl" style={{ minHeight: '320px' }}>
            <div className="animate-fade-in" style={{ animationDuration: '0.6s', animationFillMode: 'both' }}>
              <span className="inline-block px-4 py-2 rounded-full bg-accent/20 text-accent text-sm font-semibold mb-6">
                {t("hero.badge")}
              </span>
            </div>

            <h1
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight min-h-[60px] md:min-h-[72px] lg:min-h-[84px] animate-fade-in"
              style={{ animationDuration: '0.6s', animationDelay: '0.1s', animationFillMode: 'both' }}
            >
              <TypingEffect 
                texts={[t("hero.title1"), t("hero.title2"), t("hero.title3"), t("hero.title4")]}
                className="text-accent"
              />
            </h1>

            <p
              className="text-lg md:text-xl text-primary-foreground/80 mb-8 leading-relaxed animate-fade-in"
              style={{ animationDuration: '0.6s', animationDelay: '0.2s', animationFillMode: 'both' }}
            >
              {t("hero.description")}
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 animate-fade-in"
              style={{ animationDuration: '0.6s', animationDelay: '0.3s', animationFillMode: 'both' }}
            >
              <Button asChild variant="hero" size="lg">
                <a href="tel:2310270290">
                  <Phone className="h-5 w-5" />
                  {t("hero.cta")}
                </a>
              </Button>
              <Button asChild variant="heroOutline" size="lg">
                <Link to="/services">
                  {t("hero.services")}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-primary-foreground/30 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-primary-foreground/60"
            />
          </div>
        </motion.div>
      </section>

      {/* Animated Stats Section */}
      <AnimatedStats />

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("features.title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("features.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("services.title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("services.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
                {t("about.title")} <span className="text-primary">LIFTCO</span>
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {t("about.description1")}
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {t("about.description2")}
              </p>

              <ul className="space-y-3 mb-8">
                {aboutItems.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <Button asChild size="lg">
                <Link to="/about">
                  {t("about.more")}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative flex flex-col items-center gap-8"
            >
              <img
                src={modernizationImage}
                alt="Σύγχρονος ανελκυστήρας LIFTCO"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
                loading="lazy"
                width="620"
                height="400"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-primary/20 to-transparent" style={{ height: '400px' }} />
              
              {/* Animated Elevator Shaft */}
              <div className="w-full flex justify-center">
                <ElevatorShaft />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <TestimonialsCarousel />

      {/* Location Widget */}
      <LocationWidget />

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t("cta.title")}
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              {t("cta.description")}
            </p>
            <Button asChild variant="hero" size="lg">
              <a href="tel:2310270290">
                <Phone className="h-5 w-5" />
                2310 270290
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
